<script setup lang="ts">
import { EIcon } from '~/enums/EIcon';
import { EIconSize } from '~/enums/EIconSize';
import appendPxSuffix from '~/utils/appendPxSuffix';

interface IPartProps {
  size?: EIconSize | string;
  white?: boolean;
  light?: boolean;
  grey?: boolean;
  accent1?: boolean;
  disabled?: boolean;
  noMask?: boolean;
  value?: string | number;
  raw?: boolean;
  mobSize?: string;
}

export type IProps = Partial<IPartProps> & {
  icon: EIcon.TImageKey;
};
const props = withDefaults(defineProps<IProps>(), { size: EIconSize.L });

const iconUrl = computed<string>(() => `url(${EIcon.getURL(props.icon)})`);

const computeDimension = computed<string>(() => appendPxSuffix(props.size));

const mobileComputeDimension = computed<string>(() => appendPxSuffix(props.mobSize ?? props.size));

const iconItemClasses = computed(() => ({
  'icon__item_white': props.white,
  'icon__item_light': props.light,
  'icon__item_grey': props.grey,
  'icon__item_accent1': props.accent1,
  'icon__item_disabled': props.disabled,
  'icon__item_no-mask': props.noMask,
}));

const iconClasses = computed(() => ({
  icon_value: props.value,
}));
</script>

<template>
  <span
    class="icon"
    :class="iconClasses"
    :data-text-icon="props.value"
  >
    <img
      v-if="raw"
      class="icon__item"
      :src="iconUrl"
      :alt="icon"
    />
    <span
      v-else
      class="icon__item"
      :class="iconItemClasses"
    ></span>
  </span>
</template>

<style scoped lang="scss">
.icon {
  display: block;

  position: relative;
  height: fit-content;
  width: fit-content;
  cursor: pointer;

  &_value {

    &::before {
      content: attr(data-text-icon);
      position: absolute;
      top: 0;
      right: 0;
      z-index: 10;
      display: block;
      width: 12px;
      height: 12px;
      background: $color-accent-1;
      border-radius: 4px;
      color: $color-neutral-0;
      text-align: center;
      user-select: none;
      pointer-events: none;
      font-size: 8px;
      font-style: normal;
      font-weight: 400;
      line-height: 13px;
      letter-spacing: -0.16px;
    }
  }

  &__item {
    display: block;

    position: relative;
    mask-image: v-bind(iconUrl);
    width: v-bind(computeDimension);
    height: v-bind(computeDimension);
    background-color: currentColor;
    mask-size: cover;
    mask-repeat: no-repeat;

    @media ($media-mobile) {
      width: v-bind(mobileComputeDimension);
      height: v-bind(mobileComputeDimension);
    }

    &_white {
      background-color: $color-neutral-0;
    }

    &_light {
      background-color: $color-neutral-25;
    }

    &_grey {
      background-color: $color-neutral-50;
    }

    &_disabled {
      background-color: $color-neutral-25;
    }

    &_no-mask {
      mask-image: unset;
      background: v-bind(iconUrl);
      background-color: unset;
      background-repeat: no-repeat;
    }

    &_accent1 {
      background-color: $color-accent-1;
    }
  }
}
</style>
